import API_BASE_URL from '../config';
import { getAuthHeaders } from '../utils/authHeaders';
import type {
  GamificationProfile,
  GamificationSettings,
  GeoAnalytics,
  AiAnalytics,
  ReportSummary,
  HazardsListResponse,
  HazardsQuery,
} from '../types/analytics';
import {
  isAnalyticsDemoActive,
  buildDemoGamificationProfile,
  buildDemoGeoAnalytics,
  buildDemoAiAnalytics,
  buildDemoReportSummary,
} from '../tutorial/analyticsDemo';

// Service for the Analytics suite. Each call hits a dedicated backend endpoint
// that returns pre-aggregated, deterministic analytics for one page.

async function getJson<T>(path: string): Promise<T> {
  const headers = getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/${path}`, { headers });
  if (!resp.ok) {
    throw new Error(`Request failed (${resp.status}): ${resp.statusText}`);
  }
  return resp.json() as Promise<T>;
}

// Short-lived client cache for the analytics GET endpoints. Switching between the
// analytics tabs unmounts/remounts each page, so without this every tab visit
// re-hits the network (and the backend aggregations). Entries are keyed by the
// full request path (incl. query params) and also dedupe concurrent in-flight
// requests. Mutations (e.g. settings updates) clear the cache so the next read is
// fresh; the server-side Redis cache remains the source of truth for freshness.
const CLIENT_CACHE_TTL_MS = 60_000;

interface CacheEntry {
  ts: number;
  promise: Promise<unknown>;
}

const responseCache = new Map<string, CacheEntry>();

function cachedGetJson<T>(path: string): Promise<T> {
  const now = Date.now();
  const cached = responseCache.get(path);
  if (cached && now - cached.ts < CLIENT_CACHE_TTL_MS) {
    return cached.promise as Promise<T>;
  }
  const promise = getJson<T>(path).catch((err) => {
    // Evict failed responses so the next attempt retries instead of replaying the error.
    if (responseCache.get(path)?.promise === promise) {
      responseCache.delete(path);
    }
    throw err;
  });
  responseCache.set(path, { ts: now, promise });
  return promise as Promise<T>;
}

function clearAnalyticsCache(): void {
  responseCache.clear();
}

// Build the ``&start=&end=`` query suffix for the date-range filter. A null
// ``start`` is omitted so the backend treats it as "all time"; a null ``end``
// is omitted so the backend defaults to today.
function rangeQuery(start: string | null, end: string | null): string {
  let q = '';
  if (start) q += `&start=${encodeURIComponent(start)}`;
  if (end) q += `&end=${encodeURIComponent(end)}`;
  return q;
}

// Full analytics query filter set. Mirrors the community-feed filters so the B2B
// dashboard can slice every analytics page identically. ``start``/``end`` drive
// the date range; the rest are optional and omitted from the query when empty so
// they never affect the community app (which only ever passes start/end).
export interface AnalyticsFilterParams {
  start: string | null;
  end: string | null;
  severities?: string[];
  status?: string; // 'all' | 'resolved' | 'unresolved'
  regions?: string[];
  userIds?: string[];
  myHazardsOnly?: boolean;
  search?: string;
  searchField?: string; // 'description' | 'tags'
}

// Build the full ``&start=...&severities=...`` query suffix. Only non-empty
// filters are appended, so an unfiltered call collapses to the plain date range
// (identical to the community app's requests). The suffix also feeds the client
// cache key (via the request path) so distinct filter sets don't collide.
function filterQuery(f: AnalyticsFilterParams): string {
  let q = rangeQuery(f.start, f.end);
  if (f.severities && f.severities.length) {
    q += `&severities=${encodeURIComponent(f.severities.join(','))}`;
  }
  if (f.status && f.status !== 'all') {
    q += `&status=${encodeURIComponent(f.status)}`;
  }
  if (f.regions && f.regions.length) {
    q += `&regions=${encodeURIComponent(f.regions.join(','))}`;
  }
  if (f.userIds && f.userIds.length) {
    q += `&userIds=${encodeURIComponent(f.userIds.join(','))}`;
  }
  if (f.myHazardsOnly) {
    q += '&myHazardsOnly=1';
  }
  const term = (f.search || '').trim();
  if (term) {
    q += `&search=${encodeURIComponent(term)}`;
    q += `&searchField=${encodeURIComponent(f.searchField || 'description')}`;
  }
  return q;
}

async function sendJson<T>(path: string, method: string, body: unknown): Promise<T> {
  const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
  const resp = await fetch(`${API_BASE_URL}/api/${path}`, {
    method,
    headers,
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`Request failed (${resp.status}): ${resp.statusText}`);
  }
  // A write may change derived analytics; drop the client cache so reads refetch.
  clearAnalyticsCache();
  return resp.json() as Promise<T>;
}

async function getGamificationProfile(limit = 10): Promise<GamificationProfile> {
  // Tutorial demo mode: serve curated data so a data-sparse tenant still sees
  // the page working. Never touches the network or the database.
  if (isAnalyticsDemoActive()) {
    return buildDemoGamificationProfile();
  }
  return cachedGetJson<GamificationProfile>(
    `getGamificationProfile?limit=${encodeURIComponent(String(limit))}`,
  );
}

async function updateGamificationSettings(
  settings: GamificationSettings,
): Promise<{ settings: GamificationSettings; canEditSettings: boolean }> {
  return sendJson('updateGamificationSettings', 'PUT', settings);
}

async function getGeoAnalytics(
  maxPoints = 2000,
  limit = 12,
  filters: AnalyticsFilterParams = { start: null, end: null },
): Promise<GeoAnalytics> {
  if (isAnalyticsDemoActive()) {
    return buildDemoGeoAnalytics();
  }
  return cachedGetJson<GeoAnalytics>(
    `getGeoAnalytics?maxPoints=${encodeURIComponent(String(maxPoints))}&limit=${encodeURIComponent(String(limit))}${filterQuery(filters)}`,
  );
}

async function getAiAnalytics(
  limit = 15,
  filters: AnalyticsFilterParams = { start: null, end: null },
): Promise<AiAnalytics> {
  if (isAnalyticsDemoActive()) {
    return buildDemoAiAnalytics();
  }
  return cachedGetJson<AiAnalytics>(
    `getAiAnalytics?limit=${encodeURIComponent(String(limit))}${filterQuery(filters)}`,
  );
}

async function getReportSummary(
  filters: AnalyticsFilterParams = { start: null, end: null },
  limit = 8,
): Promise<ReportSummary> {
  if (isAnalyticsDemoActive()) {
    return buildDemoReportSummary();
  }
  return cachedGetJson<ReportSummary>(
    `getReportSummary?limit=${encodeURIComponent(String(limit))}${filterQuery(filters)}`,
  );
}

// Serialize a HazardsQuery into a query string. Array filters become CSV; empty
// values are omitted so the request stays compact and cache keys are stable.
function hazardsQuery(q: HazardsQuery): string {
  const parts: string[] = [
    `page=${encodeURIComponent(String(q.page))}`,
    `pageSize=${encodeURIComponent(String(q.pageSize))}`,
    `sortBy=${encodeURIComponent(q.sortBy)}`,
    `sortDir=${encodeURIComponent(q.sortDir)}`,
  ];
  const add = (key: string, value?: string | string[]) => {
    if (Array.isArray(value)) {
      if (value.length) parts.push(`${key}=${encodeURIComponent(value.join(','))}`);
    } else if (value !== undefined && value !== null && String(value).trim() !== '' && value !== 'all') {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  };
  add('recordId', q.recordId);
  add('sourceRecordId', q.sourceRecordId);
  add('dateStart', q.dateStart);
  add('dateEnd', q.dateEnd);
  add('city', q.city);
  add('province', q.province);
  add('country', q.country);
  add('reportedBy', q.reportedBy);
  add('tags', q.tags);
  add('votedRisk', q.votedRisk);
  add('riskPerception', q.riskPerception);
  add('hazardSeverity', q.hazardSeverity);
  add('consequenceType', q.consequenceType);
  add('category', q.category);
  add('description', q.description);
  add('oshaEvent', q.oshaEvent);
  add('injuryNature', q.injuryNature);
  add('bodyPart', q.bodyPart);
  add('causeGrouping', q.causeGrouping);
  add('resolved', q.resolved);
  add('resolution', q.resolution);
  add('hasImage', q.hasImage);
  return parts.join('&');
}

async function getHazardsList(q: HazardsQuery): Promise<HazardsListResponse> {
  return cachedGetJson<HazardsListResponse>(`getHazardsList?${hazardsQuery(q)}`);
}

const analyticsService = {
  getGamificationProfile,
  updateGamificationSettings,
  getGeoAnalytics,
  getAiAnalytics,
  getReportSummary,
  getHazardsList,
  clearAnalyticsCache,
};

export default analyticsService;
